import { getApi } from "./get-api";
import type { SyncChange, SyncStatus } from "./sync-state";

/**
 * Client for the sync state routes in ARD 9.6 / onlydash-api #46.
 *
 * Routes (flag-independent):
 * - GET  /sync/:datasourceId/status
 * - GET  /sync/:datasourceId/changes?status=&limit=&page=
 * - POST /sync/changes/:id/retry
 * - POST /sync/changes/:id/dismiss
 *
 * Write routes (PUT/POST/DELETE/bulk) answer 202 with `SheetsQueuedResponse`
 * `{ changes, items }` only when `SHEETS_WRITE_QUEUE=on`. With the flag off
 * they keep the old synchronous shapes. The generated client still types those
 * writes as the pre-queue swagger, so callers read `response.status` and
 * `response.data` themselves.
 */

type SyncRequest = {
  token?: string | null;
};

const request = (token: string | null | undefined, path: string, method: string) =>
  (getApi({ token }) as any).request({
    path,
    method,
    format: "json",
  });

export const fetchSyncStatus = async (
  datasourceId: string,
  options: SyncRequest = {}
): Promise<SyncStatus> => {
  const response = await request(
    options.token,
    `/sync/${datasourceId}/status`,
    "GET"
  );
  const body = response?.data ?? {};
  return {
    lastSyncedAt: body.lastSyncedAt ?? null,
    pending: Number(body.pending ?? 0),
    syncing: Number(body.syncing ?? 0),
    failed: Number(body.failed ?? 0),
    oldestPendingAt: body.oldestPendingAt ?? null,
    pausedUntil: body.pausedUntil ?? null,
  };
};

/** ARD 9.6 default, and the API maximum (`SheetsChangesQuery.limit`). */
export const SYNC_CHANGES_PAGE = 100;

/**
 * The most pages one fetch reads. 100 changes per page gives 10 000 changes.
 * Above that the markers stay right, but the list is cut short; a client that
 * never stops would spin if the API ignored the `page` parameter.
 */
export const SYNC_CHANGES_MAX_PAGES = 100;

const changePage = async (
  datasourceId: string,
  token: string | null | undefined,
  status: string,
  page: number
): Promise<SyncChange[]> => {
  const response = await request(
    token,
    `/sync/${datasourceId}/changes?status=${status}&limit=${SYNC_CHANGES_PAGE}&page=${page}`,
    "GET"
  );
  const body = response?.data;
  // Accept a bare array, `{ items }`, or `{ changes }` (same key as the 202
  // write body) so a swagger rename does not blank the markers.
  const items = Array.isArray(body)
    ? body
    : body?.items ?? body?.changes;
  return Array.isArray(items) ? (items as SyncChange[]) : [];
};

/**
 * Every queued or failed change for one datasource (ARD 9.6).
 *
 * The API reads one page of at most 100 changes (`page` starts at 1). This
 * function reads pages until a page comes back short, so a failed change on
 * page 2 is not lost. A short or empty page ends the read.
 */
export const fetchSyncChanges = async (
  datasourceId: string,
  options: SyncRequest = {},
  statuses: SyncChange["status"][] = ["PENDING", "SYNCING", "FAILED"]
): Promise<SyncChange[]> => {
  const status = encodeURIComponent(statuses.join(","));
  const all: SyncChange[] = [];
  for (let page = 1; page <= SYNC_CHANGES_MAX_PAGES; page += 1) {
    const items = await changePage(datasourceId, options.token, status, page);
    all.push(...items);
    if (items.length < SYNC_CHANGES_PAGE) break;
  }
  return all;
};

export const retrySyncChange = async (
  changeId: string,
  options: SyncRequest = {}
): Promise<void> => {
  await request(options.token, `/sync/changes/${changeId}/retry`, "POST");
};

export const dismissSyncChange = async (
  changeId: string,
  options: SyncRequest = {}
): Promise<void> => {
  await request(options.token, `/sync/changes/${changeId}/dismiss`, "POST");
};
